import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';

const SESSION_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds

// Helper to get default dates
const getDefaultDates = () => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  return { checkIn: formatDate(today), checkOut: formatDate(tomorrow) };
};

export const useSessionTimeout = () => {
  const router = useRouter();
  const timeoutRef = useRef(null);

  const resetSession = () => {
    // IMPORTANT: Save property from URL before clearing
    let savedProperty = null;
    
    if (typeof window !== 'undefined') {
      // First, try to get property from current URL
      const urlParams = new URLSearchParams(window.location.search);
      const urlProperty = urlParams.get('property');
      
      if (urlProperty && urlProperty !== 'null' && urlProperty !== 'undefined' && urlProperty.trim() !== '') {
        savedProperty = urlProperty;
        console.log('💾 Session timeout: Saving property from URL:', savedProperty);
      } else {
        // Try to get from localStorage before clearing
        try {
          const storedData = localStorage.getItem('selectedProperty');
          if (storedData) {
            const parsed = JSON.parse(storedData);
            savedProperty = parsed.code || parsed;
            console.log('💾 Session timeout: Retrieved property from localStorage:', savedProperty);
          }
        } catch (e) {
          console.warn('Error reading property from localStorage:', e);
        }
      }
    }
    
    // Clear all cookies
    document.cookie.split(";").forEach(cookie => {
      document.cookie = cookie
        .replace(/^ +/, "")
        .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
    });
    
    // Clear localStorage
    localStorage.clear();
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    // Re-save the property after clearing
    if (savedProperty) {
      try {
        localStorage.setItem('selectedProperty', JSON.stringify({
          code: savedProperty,
          description: savedProperty,
          timestamp: Date.now()
        }));
        console.log('💾 Session timeout: Property saved to localStorage:', savedProperty);
      } catch (e) {
        console.warn('Error saving property to localStorage:', e);
      }
    }
    
    // Build redirect URL with property
    const { checkIn, checkOut } = getDefaultDates();
    const propertyToUse = savedProperty || 'DEMO';
    const redirectUrl = `/?checkIn=${checkIn}&checkOut=${checkOut}&adults=2&children=0&room=1&property=${propertyToUse}&sort=price-asc`;
    
    console.log('🔄 Session timeout: Redirecting to:', redirectUrl);
    
    // Redirect with property preserved
    window.location.href = redirectUrl;
  };

  const setupTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(resetSession, SESSION_TIMEOUT);
  };

  useEffect(() => {
    // Set up initial timeout
    setupTimeout();

    // Reset timeout on user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    const handleUserActivity = () => {
      setupTimeout();
    };

    events.forEach(event => {
      document.addEventListener(event, handleUserActivity);
    });

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, []);

  return resetSession;
};
